import {useEffect, useRef, useState} from 'react'
import {
  Dimensions,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import {LinearGradient} from 'expo-linear-gradient'
import {Feather} from '@expo/vector-icons'

import {type ThemePlayerProps} from '#/types/capsule'

const {width: _SW} = Dimensions.get('window')

const EMOJIS: Record<string, string> = {
  single_choice: '☀️',
  multiple_choice: '🌺',
  true_false: '🌊',
  matching: '🐠',
  fill_blank: '🌴',
  ordering: '🦀',
  image_choice: '🌅',
  content: '🏖️',
  video: '🎬',
}

function QuizBadge({type}: {type: string}) {
  const labels: Record<string, string> = {
    single_choice: 'SINGLE CHOICE',
    multiple_choice: 'MULTIPLE CHOICE',
    true_false: 'TRUE OR FALSE',
    matching: 'MATCHING',
    fill_blank: 'FILL IN THE BLANK',
    ordering: 'ORDERING',
    image_choice: 'IMAGE CHOICE',
    content: 'CONTENT',
    video: 'VIDEO',
  }
  return (
    <View style={s.badge}>
      <Text style={s.badgeTxt}>{labels[type] ?? type.toUpperCase()}</Text>
    </View>
  )
}

export function PlayerComponent({
  capsule,
  currentPageIndex: _currentPageIndex,
  onPageComplete,
  onCapsuleComplete,
}: ThemePlayerProps) {
  const insets = useSafeAreaInsets()
  const [answered, setAnswered] = useState(false)
  const [selected, setSelected] = useState<string[]>([])
  const [blankAnswers, setBlankAnswers] = useState<Record<string, string>>({})
  const [matchAnswers, setMatchAnswers] = useState<Record<string, string>>({})
  const [showResult, setShowResult] = useState(false)
  const [results, setResults] = useState<
    {
      pageId: string
      isCorrect: boolean
      timeSpentMs: number
      userAnswer: unknown
    }[]
  >([])
  const [currentIdx, setCurrentIdx] = useState(0)
  const startTime = useRef(Date.now())

  const slides = capsule.slides ?? []
  const page = slides[currentIdx]
  const isLast = currentIdx === slides.length - 1
  const progress = slides.length > 0 ? (currentIdx + 1) / slides.length : 0
  const emoji = page ? (EMOJIS[page.type] ?? '🌴') : '🌴'
  const settings = capsule.behaviorSettings as {
    primaryColor?: string
    emoji?: string
  }
  const headerEmoji = settings?.emoji ?? '🌴'
  const primaryColor = settings?.primaryColor ?? '#f59e0b'

  useEffect(() => {
    setAnswered(false)
    setSelected([])
    setBlankAnswers({})
    setMatchAnswers({})
    startTime.current = Date.now()
  }, [currentIdx])

  if (showResult) {
    const correctCount = results.filter(r => r.isCorrect).length
    const total = results.length
    const pct = total > 0 ? Math.round((correctCount / total) * 100) : 100
    const timeMs = results.reduce((sum, r) => sum + (r.timeSpentMs ?? 0), 0)
    const timeSec = Math.round(timeMs / 1000)

    return (
      <LinearGradient
        colors={['#FFF8E7', '#FFE4B5', '#FDD870']}
        style={[s.root, {paddingTop: insets.top}]}>
        <View style={[s.resultHeader]}>
          <Text style={s.resultEmoji}>🏆</Text>
          <Text style={s.resultTitle}>Holiday wrapped!</Text>
        </View>
        <View style={s.resultCircle}>
          <Text style={s.resultPct}>{pct}%</Text>
          <Text style={s.resultPctLabel}>SCORE</Text>
        </View>
        <View style={s.resultStats}>
          <View style={s.resultStat}>
            <Text style={[s.resultStatNum, {color: primaryColor}]}>
              {correctCount}
            </Text>
            <Text style={s.resultStatLabel}>CORRECT</Text>
          </View>
          <View style={s.resultStat}>
            <Text style={[s.resultStatNum, {color: primaryColor}]}>
              {total}
            </Text>
            <Text style={s.resultStatLabel}>TOTAL</Text>
          </View>
          <View style={s.resultStat}>
            <Text style={[s.resultStatNum, {color: primaryColor}]}>
              {timeSec}s
            </Text>
            <Text style={s.resultStatLabel}>TIME</Text>
          </View>
        </View>
        <View style={s.resultBtns}>
          <TouchableOpacity
            accessibilityRole="button"
            style={s.replayBtn}
            onPress={() => {
              setCurrentIdx(0)
              setResults([])
              setAnswered(false)
              setSelected([])
              setShowResult(false)
              startTime.current = Date.now()
            }}>
            <Text style={s.replayTxt}>Replay</Text>
          </TouchableOpacity>
          <TouchableOpacity
            accessibilityRole="button"
            style={[s.doneBtn, {backgroundColor: primaryColor}]}
            onPress={() => onCapsuleComplete(results)}>
            <Text style={s.doneTxt}>Done ✨</Text>
          </TouchableOpacity>
        </View>
        <Text style={[s.deco1]}>{headerEmoji}</Text>
        <Text style={[s.deco2]}>🏖️</Text>
      </LinearGradient>
    )
  }

  const handleSelect = (label: string) => {
    if (answered) return
    if (page?.type === 'multiple_choice') {
      setSelected(prev =>
        prev.includes(label) ? prev.filter(x => x !== label) : [...prev, label],
      )
    } else {
      setSelected([label])
    }
  }

  const handleSubmit = () => {
    if (!page) return
    setAnswered(true)

    let isCorrect = false
    let userAnswer: unknown = selected

    const type = page.type
    if (type === 'true_false') {
      isCorrect =
        selected[0] === page.answer ||
        selected[0]?.toLowerCase() === page.answer
    } else if (type === 'multiple_choice') {
      const correctSet = new Set(
        page.options?.filter(o => o.isCorrect).map(o => o.label) ?? [],
      )
      const selectedSet = new Set(selected)
      isCorrect =
        correctSet.size === selectedSet.size &&
        [...correctSet].every(x => selectedSet.has(x))
    } else if (type === 'fill_blank') {
      isCorrect = (page.blanks ?? []).every(b => {
        const ua = (blankAnswers[b.id] ?? '').trim().toLowerCase()
        return (
          ua === b.answer.trim().toLowerCase() ||
          (b.alternatives ?? []).some(a => a.trim().toLowerCase() === ua)
        )
      })
      userAnswer = blankAnswers
    } else if (type === 'matching') {
      isCorrect = (page.pairs ?? []).every(
        p => matchAnswers[p.id]?.trim() === p.right.trim(),
      )
      userAnswer = matchAnswers
    } else if (type === 'ordering') {
      const correct = page.correctOrder ?? []
      isCorrect =
        selected.length === correct.length &&
        selected.every((id, i) => id === correct[i])
    } else {
      isCorrect = !!page.options?.find(
        o => o.isCorrect && selected.includes(o.label),
      )
    }

    const result = {
      pageId: page.id,
      isCorrect,
      timeSpentMs: Date.now() - startTime.current,
      userAnswer,
    }

    const newResults = [...results, result]
    setResults(newResults)
    onPageComplete(result)
  }

  const handleNext = () => {
    if (isLast) {
      setShowResult(true)
    } else {
      setCurrentIdx(i => i + 1)
      setAnswered(false)
      setSelected([])
      startTime.current = Date.now()
    }
  }

  const isVideoSlide = page?.type === 'video'
  const isContentSlide = page?.type === 'content'

  return (
    <LinearGradient
      colors={['#FFF8E7', '#FFE4B5', '#FDD870']}
      style={[s.root, {paddingTop: insets.top}]}>
      <View style={s.header}>
        <View style={s.headerLeft}>
          <Text style={s.headerTitle}>
            {capsule.title?.toUpperCase() ?? 'CAPSULE'}
          </Text>
          <Text style={s.headerSub}>{slides.length} slides</Text>
        </View>
        <TouchableOpacity
          accessibilityRole="button"
          style={s.closeBtn}
          onPress={() => onCapsuleComplete(results)}>
          <Feather name="x" size={16} color="#333" />
        </TouchableOpacity>
      </View>

      <View style={s.progressBg}>
        <View
          style={[
            s.progressFill,
            {width: `${progress * 100}%`, backgroundColor: '#e55'},
          ]}
        />
      </View>

      <ScrollView
        contentContainerStyle={s.scroll}
        showsVerticalScrollIndicator={false}>
        <View style={s.emojiRow}>
          <Text style={s.mainEmoji}>{headerEmoji}</Text>
        </View>

        <Text style={s.capsuleTitle}>{capsule.title}</Text>
        {capsule.description ? (
          <Text style={s.capsuleDesc}>{capsule.description}</Text>
        ) : null}

        <View style={s.card}>
          {!isVideoSlide && !isContentSlide && (
            <QuizBadge type={page?.type ?? ''} />
          )}

          {isVideoSlide ? (
            <View style={s.videoPlaceholder}>
              <Text style={s.videoEmoji}>🎬</Text>
              <Text style={s.videoLabel}>Vidéo du professeur</Text>
              <TouchableOpacity
                accessibilityRole="button"
                style={[s.startBtn, {backgroundColor: primaryColor}]}
                onPress={handleNext}>
                <Text style={s.startBtnTxt}>Commencer ▶</Text>
              </TouchableOpacity>
            </View>
          ) : isContentSlide ? (
            <View style={{gap: 8}}>
              {page?.contentTitle ? (
                <Text style={s.contentTitle}>{page.contentTitle}</Text>
              ) : null}
              {page?.contentText ? (
                <Text style={s.explanationTxt}>{page.contentText}</Text>
              ) : null}
            </View>
          ) : (
            <>
              <Text style={s.questionText}>{page?.question}</Text>

              {/* Single / Multiple / Image choice */}
              {(page?.type === 'single_choice' ||
                page?.type === 'multiple_choice' ||
                page?.type === 'image_choice') && (
                <View style={s.options}>
                  {(page.options ?? []).map(opt => {
                    const isSel = selected.includes(opt.label)
                    const isCorrectOpt = opt.isCorrect
                    const isWrong = answered && isSel && !isCorrectOpt
                    const isRight = answered && isCorrectOpt
                    return (
                      <TouchableOpacity
                        accessibilityRole="button"
                        key={opt.label}
                        style={[
                          s.option,
                          isSel &&
                            !answered && {
                              backgroundColor: primaryColor + '22',
                              borderColor: primaryColor,
                            },
                          isRight && {
                            backgroundColor: '#22c55e18',
                            borderColor: '#22c55e',
                          },
                          isWrong && {
                            backgroundColor: '#ef444418',
                            borderColor: '#ef4444',
                          },
                        ]}
                        onPress={() => handleSelect(opt.label)}
                        activeOpacity={0.7}>
                        <Text style={s.optEmoji}>{emoji}</Text>
                        <Text style={s.optLabel}>{opt.label}</Text>
                        {answered && isCorrectOpt && (
                          <Feather
                            name="check-circle"
                            size={16}
                            color="#22c55e"
                          />
                        )}
                        {answered && isSel && !isCorrectOpt && (
                          <Feather name="x-circle" size={16} color="#ef4444" />
                        )}
                      </TouchableOpacity>
                    )
                  })}
                </View>
              )}

              {/* True / False */}
              {page?.type === 'true_false' && (
                <View style={s.tfRow}>
                  {(['true', 'false'] as const).map(val => {
                    const isSel = selected.includes(val)
                    const isRight = answered && val === page.answer
                    const isWrong = answered && isSel && val !== page.answer
                    return (
                      <TouchableOpacity
                        accessibilityRole="button"
                        key={val}
                        style={[
                          s.tfBtn,
                          isSel &&
                            !answered && {
                              backgroundColor: primaryColor + '22',
                              borderColor: primaryColor,
                            },
                          isRight && {
                            backgroundColor: '#22c55e22',
                            borderColor: '#22c55e',
                          },
                          isWrong && {
                            backgroundColor: '#ef444422',
                            borderColor: '#ef4444',
                          },
                        ]}
                        onPress={() => handleSelect(val)}
                        activeOpacity={0.7}>
                        <Text style={s.tfEmoji}>
                          {val === 'true' ? '✅' : '❌'}
                        </Text>
                        <Text style={s.tfLabel}>
                          {val === 'true' ? 'True' : 'False'}
                        </Text>
                      </TouchableOpacity>
                    )
                  })}
                </View>
              )}

              {/* Fill in the blank */}
              {page?.type === 'fill_blank' && (
                <View style={s.options}>
                  {(page.blanks ?? []).map((blank, idx) => {
                    const ua = blankAnswers[blank.id] ?? ''
                    const isCorrect =
                      answered &&
                      (ua.trim().toLowerCase() ===
                        blank.answer.trim().toLowerCase() ||
                        (blank.alternatives ?? []).some(
                          a =>
                            a.trim().toLowerCase() === ua.trim().toLowerCase(),
                        ))
                    const isWrong = answered && !isCorrect
                    return (
                      <View key={blank.id} style={{gap: 4}}>
                        {blank.before ? (
                          <Text style={s.explanationTxt}>{blank.before}</Text>
                        ) : null}
                        <TextInput
                          accessibilityLabel="Text input field"
                          accessibilityHint="Enter your answer for this blank"
                          style={[
                            s.blankInput,
                            isCorrect && {borderColor: '#22c55e'},
                            isWrong && {borderColor: '#ef4444'},
                          ]}
                          value={ua}
                          onChangeText={v =>
                            setBlankAnswers(prev => ({...prev, [blank.id]: v}))
                          }
                          placeholder={`Blank ${idx + 1}`}
                          placeholderTextColor="#aaa"
                          editable={!answered}
                        />
                        {blank.after ? (
                          <Text style={s.explanationTxt}>{blank.after}</Text>
                        ) : null}
                        {answered && isWrong && (
                          <Text style={{color: '#22c55e', fontSize: 12}}>
                            → {blank.answer}
                          </Text>
                        )}
                      </View>
                    )
                  })}
                </View>
              )}

              {/* Matching */}
              {page?.type === 'matching' && (
                <View style={s.options}>
                  {(page.pairs ?? []).map(pair => {
                    const ua = matchAnswers[pair.id] ?? ''
                    const isCorrect =
                      answered && ua.trim() === pair.right.trim()
                    const isWrong = answered && !isCorrect
                    return (
                      <View key={pair.id} style={s.matchRow}>
                        <View style={s.matchLeft}>
                          <Text style={s.matchLeftTxt}>{pair.left}</Text>
                        </View>
                        <Text style={s.pairArrow}>↔</Text>
                        <TextInput
                          accessibilityLabel="Text input field"
                          accessibilityHint="Enter the matching value"
                          style={[
                            s.matchInput,
                            isCorrect && {borderColor: '#22c55e'},
                            isWrong && {borderColor: '#ef4444'},
                          ]}
                          value={ua}
                          onChangeText={v =>
                            setMatchAnswers(prev => ({...prev, [pair.id]: v}))
                          }
                          placeholder="Match…"
                          placeholderTextColor="#aaa"
                          editable={!answered}
                        />
                      </View>
                    )
                  })}
                </View>
              )}

              {/* Ordering */}
              {page?.type === 'ordering' && (
                <View style={s.options}>
                  <Text style={{color: '#888', fontSize: 12, marginBottom: 4}}>
                    Tap in the correct order:
                  </Text>
                  {(page.orderItems ?? []).map(item => {
                    const pos = selected.indexOf(item.id)
                    const isSel = pos !== -1
                    const correctIdx = (page.correctOrder ?? []).indexOf(
                      item.id,
                    )
                    const isRight = answered && isSel && pos === correctIdx
                    const isWrong = answered && isSel && pos !== correctIdx
                    return (
                      <TouchableOpacity
                        accessibilityRole="button"
                        key={item.id}
                        disabled={answered || isSel}
                        onPress={() => setSelected(prev => [...prev, item.id])}
                        style={[
                          s.option,
                          isSel &&
                            !answered && {
                              backgroundColor: primaryColor + '22',
                              borderColor: primaryColor,
                            },
                          isRight && {
                            backgroundColor: '#22c55e18',
                            borderColor: '#22c55e',
                          },
                          isWrong && {
                            backgroundColor: '#ef444418',
                            borderColor: '#ef4444',
                          },
                        ]}
                        activeOpacity={0.7}>
                        {isSel && (
                          <View
                            style={[
                              s.orderBadge,
                              {backgroundColor: primaryColor},
                            ]}>
                            <Text style={s.orderBadgeTxt}>{pos + 1}</Text>
                          </View>
                        )}
                        <Text style={s.optEmoji}>{emoji}</Text>
                        <Text style={s.optLabel}>{item.label}</Text>
                      </TouchableOpacity>
                    )
                  })}
                  {selected.length > 0 && !answered && (
                    <TouchableOpacity
                      accessibilityRole="button"
                      onPress={() => setSelected([])}>
                      <Text
                        style={{
                          color: primaryColor,
                          fontSize: 12,
                          textAlign: 'right',
                        }}>
                        Reset order
                      </Text>
                    </TouchableOpacity>
                  )}
                </View>
              )}

              {answered && page?.explanation ? (
                <View style={s.explanation}>
                  <Text style={s.explanationTxt}>💡 {page.explanation}</Text>
                </View>
              ) : null}
            </>
          )}
        </View>
      </ScrollView>

      {!isVideoSlide && (
        <View style={[s.footer, {paddingBottom: insets.bottom + 12}]}>
          {answered ? (
            <TouchableOpacity
              accessibilityRole="button"
              style={[s.submitBtn, {backgroundColor: primaryColor}]}
              onPress={handleNext}>
              <Text style={s.submitTxt}>
                {isLast ? 'Voir les résultats ✨' : 'Continuer →'}
              </Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              accessibilityRole="button"
              style={[
                s.submitBtn,
                {
                  backgroundColor: selected.length > 0 ? '#e05' : '#ccc',
                  opacity: selected.length > 0 ? 1 : 0.6,
                },
              ]}
              onPress={handleSubmit}
              disabled={selected.length === 0}>
              <Text style={s.submitTxt}>Submit ✨</Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      <Text style={s.deco1}>{headerEmoji}</Text>
      <Text style={s.deco2}>🏖️</Text>
    </LinearGradient>
  )
}

const s = StyleSheet.create({
  root: {flex: 1},
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 12,
  },
  headerLeft: {gap: 1},
  headerTitle: {
    fontSize: 12,
    fontWeight: '800',
    color: '#333',
    letterSpacing: 1,
  },
  headerSub: {fontSize: 11, color: '#888'},
  closeBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(0,0,0,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  progressBg: {height: 4, backgroundColor: 'rgba(0,0,0,0.1)'},
  progressFill: {height: '100%', borderRadius: 2},
  scroll: {
    paddingHorizontal: 20,
    paddingBottom: 100,
    alignItems: 'center',
    gap: 12,
  },
  emojiRow: {marginTop: 20},
  mainEmoji: {fontSize: 48},
  capsuleTitle: {
    fontSize: 18,
    fontWeight: '800',
    color: '#1a1a1a',
    textAlign: 'center',
  },
  capsuleDesc: {
    fontSize: 13,
    color: '#555',
    textAlign: 'center',
    lineHeight: 18,
  },
  card: {
    width: '100%',
    backgroundColor: '#fff',
    borderRadius: 22,
    padding: 20,
    gap: 14,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 4,
  },
  badge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 4,
    backgroundColor: '#FDD870',
    borderRadius: 20,
  },
  badgeTxt: {
    fontSize: 10,
    fontWeight: '800',
    color: '#9b6800',
    letterSpacing: 0.5,
  },
  questionText: {
    fontSize: 18,
    fontWeight: '800',
    color: '#1a1a1a',
    lineHeight: 26,
  },
  options: {gap: 10},
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderRadius: 16,
    backgroundColor: '#FFF8E7',
    borderWidth: 1.5,
    borderColor: '#FFE4B5',
  },
  optEmoji: {fontSize: 18},
  optLabel: {flex: 1, fontSize: 14, fontWeight: '600', color: '#1a1a1a'},
  tfRow: {flexDirection: 'row', gap: 12},
  tfBtn: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 18,
    borderRadius: 16,
    backgroundColor: '#FFF8E7',
    borderWidth: 1.5,
    borderColor: '#FFE4B5',
  },
  tfEmoji: {fontSize: 28},
  tfLabel: {fontSize: 16, fontWeight: '800', color: '#1a1a1a'},
  explanation: {
    backgroundColor: '#FFFDF0',
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: '#FFE4B5',
  },
  explanationTxt: {fontSize: 13, color: '#555', lineHeight: 18},
  videoPlaceholder: {alignItems: 'center', gap: 14, paddingVertical: 20},
  videoEmoji: {fontSize: 52},
  videoLabel: {fontSize: 14, color: '#888', fontWeight: '600'},
  startBtn: {paddingHorizontal: 28, paddingVertical: 12, borderRadius: 24},
  startBtnTxt: {color: '#fff', fontSize: 15, fontWeight: '800'},
  contentTitle: {fontSize: 18, fontWeight: '700', color: '#1a1a1a'},
  footer: {paddingHorizontal: 20, paddingTop: 12},
  submitBtn: {
    paddingVertical: 16,
    borderRadius: 24,
    alignItems: 'center',
  },
  submitTxt: {color: '#fff', fontSize: 15, fontWeight: '800'},
  resultHeader: {alignItems: 'center', paddingTop: 40, gap: 8},
  resultEmoji: {fontSize: 56},
  resultTitle: {
    fontSize: 24,
    fontWeight: '800',
    color: '#1a1a1a',
    textAlign: 'center',
  },
  resultCircle: {
    width: 130,
    height: 130,
    borderRadius: 65,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    marginVertical: 24,
    shadowColor: '#000',
    shadowOpacity: 0.08,
    shadowRadius: 16,
    elevation: 4,
  },
  resultPct: {fontSize: 36, fontWeight: '900', color: '#1a1a1a'},
  resultPctLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: '#888',
    letterSpacing: 1,
  },
  resultStats: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 32,
    marginBottom: 32,
  },
  resultStat: {alignItems: 'center', gap: 4},
  resultStatNum: {fontSize: 28, fontWeight: '800'},
  resultStatLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: '#888',
    letterSpacing: 1,
  },
  resultBtns: {
    flexDirection: 'row',
    gap: 12,
    paddingHorizontal: 24,
  },
  replayBtn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 24,
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.08)',
  },
  replayTxt: {fontSize: 14, fontWeight: '700', color: '#333'},
  doneBtn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 24,
    alignItems: 'center',
  },
  doneTxt: {color: '#fff', fontSize: 15, fontWeight: '800'},
  deco1: {
    position: 'absolute',
    fontSize: 80,
    opacity: 0.06,
    top: -10,
    right: -10,
    pointerEvents: 'none',
  },
  deco2: {
    position: 'absolute',
    fontSize: 60,
    opacity: 0.05,
    bottom: 80,
    left: -10,
    pointerEvents: 'none',
  },
  blankInput: {
    color: '#1a1a1a',
    fontSize: 14,
    backgroundColor: '#fff',
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: '#FFE4B5',
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  matchRow: {flexDirection: 'row', alignItems: 'center', gap: 8},
  matchLeft: {
    flex: 1,
    backgroundColor: '#FFF8E7',
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: '#FFE4B5',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  matchLeftTxt: {color: '#1a1a1a', fontSize: 13, fontWeight: '600'},
  pairArrow: {color: '#aaa', fontSize: 14},
  matchInput: {
    flex: 1,
    color: '#1a1a1a',
    fontSize: 13,
    backgroundColor: '#fff',
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: '#FFE4B5',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  orderBadge: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 4,
  },
  orderBadgeTxt: {color: '#fff', fontSize: 11, fontWeight: '800'},
})
